import type { ISelfReminderNotificationData } from "@frontend-shared/types";
import { Bell } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { NotificationCardShell } from "../NotificationCardShell";

interface ISelfReminderCardProps {
  uuid: string;
  data: ISelfReminderNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewRuns: (automationUuid: string, projectUuid?: string) => void;
}

const SelfReminderCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewRuns,
}: ISelfReminderCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");

  const handleViewRuns = useCallback((): void => {
    if (!data.automationUuid) {
      return;
    }
    onViewRuns(data.automationUuid, data.projectUuid);
  }, [data.automationUuid, data.projectUuid, onViewRuns]);

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="self_reminder"
      createdAt={createdAt}
      isRead={isRead}
      icon={<Bell className="h-5 w-5" />}
      ctaLabel={data.automationUuid ? t("view_runs") : undefined}
      onCtaClick={data.automationUuid ? handleViewRuns : undefined}
      body={
        <div className="space-y-1">
          <p className="font-medium text-foreground">{data.title}</p>
          {/* NOTE: Scroll wide tables/code instead of clipping them
              (`[&_table]:block` is needed for table overflow to apply). */}
          <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_a]:text-primary [&_pre]:overflow-x-auto [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {data.body}
            </ReactMarkdown>
          </div>
        </div>
      }
    />
  );
};

export const SelfReminderCard = memo(SelfReminderCardComponent);
